import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, ArrowDown, Flag, Loader2, Mic, Send, Square, Volume2, X } from "lucide-react";
import { api } from "@/lib/api";
import { renderText } from "@/lib/renderText";
import { Button } from "@/components/ui/button";
import { useCustomerView } from "@/context/CustomerViewContext";
import { useCopilot } from "@/context/CopilotContext";
import { toast } from "sonner";
import { isGovernedTarget } from "@/lib/navigation";
import { ActionCard } from "./ActionCard";
import { GovernedLink } from "./GovernedLink";
import { CardList, FOCUS_RING, LiveRegion, LongText } from "./Disclosure";

// V5-H4 UX-007: cards that carry warnings, amounts, status, confirmation or required actions are
// NEVER collapsed; only homogeneous result lists (products / records / projects) use "Show N more".
const NEVER_COLLAPSE = new Set(["action", "nba", "calculation", "estimate", "summary", "pricing", "technical", "navigation", "document_binding"]);

// The single governed conversation surface used by BOTH the contextual launcher
// panel and the full Copilot workspace (COP-UX-136) — voice and text enter the same
// session, the same context and the same authorization boundary (COP-UX-138).

function ProductCard({ card, onAsk, busy }) {
  return (
    <div className="rounded-md border border-border bg-white p-3" data-testid="copilot-product-card">
      <div className="flex gap-3">
        {card.image && (
          <img src={card.image} alt={card.label} className="h-14 w-14 rounded-sm object-cover" />
        )}
        <div className="min-w-0 flex-1">
          <p className="break-words text-sm font-medium text-charcoal">{card.label}</p>
          <p className="text-xs text-warmgrey">
            {card.sku && <span className="mr-1 font-mono font-semibold text-charcoal" data-testid="copilot-card-sku">{card.sku}</span>}
            {[card.composition ? card.composition.map((x) => `${x.name}${x.share_pct ? ` ${x.share_pct}%` : ""}`).join(" + ") : card.color_name, card.format_in, card.presentation].filter(Boolean).join(" · ")}
          </p>
          {(card.factory_color_name && !String(card.label || "").includes(card.factory_color_name) || (card.composition && card.composition.some((x) => x.factory_name && x.factory_name !== x.name))) && (
            <p className="text-[11px] text-muted-foreground" data-testid="copilot-card-factory-name">
              Factory name: {card.composition ? card.composition.map((x) => x.factory_name).filter(Boolean).join(" + ") : card.factory_color_name}
            </p>
          )}
          <p className="mt-1 text-xs text-charcoal" data-testid="copilot-card-availability">
            {card.florida_available > 0
              ? `${card.florida_available} boxes available now`
              : card.incoming_available > 0
                ? `${card.incoming_available} boxes incoming`
                : "At origin — ask for timing"}
          </p>
          {card.retail_price_per_sqft != null && (
            <p className="text-xs text-charcoal">
              <span className="num font-semibold">${Number(card.retail_price_per_sqft).toFixed(2)}</span>
              <span className="text-warmgrey">/sq ft</span>
            </p>
          )}
          {card.trade_price_per_sqft != null && (
            <p className="text-xs text-warmgrey" data-testid="copilot-card-trade-price">
              Trade <span className="num">${Number(card.trade_price_per_sqft).toFixed(2)}</span>/sq ft
            </p>
          )}
        </div>
      </div>
      <CardActions card={card} onAsk={onAsk} busy={busy} />
    </div>
  );
}

function ProjectCard({ card, onAsk, busy }) {
  return (
    <div className="rounded-md border border-border bg-white p-3" data-testid="copilot-project-card">
      <p className="text-sm font-medium text-charcoal">{card.name}</p>
      <p className="text-xs text-warmgrey">
        {card.stage}{card.projected_sqft ? ` · ${card.projected_sqft} sqft` : ""}{card.client_name ? ` · ${card.client_name}` : ""}
      </p>
      <CardActions card={card} onAsk={onAsk} busy={busy} />
    </div>
  );
}

function CalculationCard({ card }) {
  return (
    <div className="rounded-md border border-border bg-white p-3" data-testid="copilot-calculation-card">
      <p className="text-xs font-semibold uppercase tracking-wide text-terracotta">
        Calculation · {card.variant}
      </p>
      {card.status === "complete" ? (
        <div className="mt-1.5 space-y-1 text-xs text-charcoal">
          {card.inputs?.dimensions && (
            <p>Dimensions: {card.inputs.dimensions.width} × {card.inputs.dimensions.height} {card.inputs.dimensions.unit}</p>
          )}
          {card.inputs?.area_sqft != null && <p>Area: <span className="num">{card.inputs.area_sqft}</span> sq ft</p>}
          <p className="text-warmgrey">{card.inputs?.overage_pct != null ? `Overage: ${card.inputs.overage_pct}%` : card.inputs?.overage_note}</p>
          {card.derived?.boxes_required != null && (
            <p className="mt-1 text-base font-semibold text-charcoal" data-testid="copilot-calc-boxes">
              <span className="num">{card.derived.boxes_required}</span> boxes
              <span className="ml-2 text-xs font-normal text-warmgrey">
                covers <span className="num">{card.derived.purchased_coverage_sqft}</span> sq ft
              </span>
            </p>
          )}
          {card.derived?.purchased_coverage_sqft != null && card.derived?.boxes_required == null && (
            <p className="mt-1 text-base font-semibold text-charcoal">
              <span className="num">{card.derived.purchased_coverage_sqft}</span> sq ft of coverage
            </p>
          )}
          <p className="text-[10px] text-warmgrey">{card.formula}</p>
          <p className="text-[10px] italic text-warmgrey">{card.note}</p>
        </div>
      ) : (
        <p className="mt-1.5 text-xs text-amber-700" data-testid="copilot-calc-clarify">{card.reason}</p>
      )}
    </div>
  );
}

function EstimateCard({ card }) {
  return (
    <div className="rounded-md border border-border bg-white p-3" data-testid="copilot-estimate-card">
      <p className="text-xs font-semibold uppercase tracking-wide text-terracotta">Estimate · {card.variant}</p>
      {card.status === "complete" ? (
        <div className="mt-1.5 space-y-1 text-xs text-charcoal">
          <p><span className="num">{card.boxes}</span> boxes · <span className="num">{card.coverage_sqft}</span> sq ft · {card.basis}</p>
          {card.retail_subtotal != null && (
            <p>Retail subtotal <span className="num font-semibold">${Number(card.retail_subtotal).toFixed(2)}</span>
              <span className="text-warmgrey"> (${Number(card.retail_price_per_sqft).toFixed(2)}/sq ft)</span></p>
          )}
          {card.trade_subtotal != null && (
            <p data-testid="copilot-estimate-trade">Trade subtotal <span className="num font-semibold">${Number(card.trade_subtotal).toFixed(2)}</span>
              <span className="text-warmgrey"> (${Number(card.trade_price_per_sqft).toFixed(2)}/sq ft)</span></p>
          )}
          <p className="text-[10px] text-warmgrey">Excludes {(card.excludes || []).join(", ")}. {card.note}</p>
        </div>
      ) : (
        <p className="mt-1.5 text-xs text-amber-700">{card.reason}</p>
      )}
    </div>
  );
}

function TechnicalCard({ card, onAsk, busy }) {
  const s = card.summary;
  return (
    <div className="rounded-md border border-border bg-white p-3" data-testid="copilot-technical-card">
      <p className="text-xs font-semibold uppercase tracking-wide text-terracotta">Technical · {card.variant}</p>
      {s && (
        <div className="mt-1.5 space-y-1.5">
          <p className="text-sm text-charcoal" data-testid="copilot-technical-answer">{s.answer}</p>
          {s.rationale && <p className="text-xs text-warmgrey" data-testid="copilot-technical-rationale">{s.rationale}</p>}
          {(s.limitations || []).length > 0 && (
            <div className="rounded-sm bg-amber-50 p-2" data-testid="copilot-technical-limitations">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-amber-800">Limitations</p>
              {s.limitations.map((l, i) => <p key={i} className="text-xs text-amber-800">{l}</p>)}
            </div>
          )}
        </div>
      )}
      <details className="mt-2" data-testid="copilot-technical-details">
        <summary className="cursor-pointer text-[11px] font-medium text-terracotta hover:underline">
          Supporting facts &amp; records{s?.supporting_count != null ? ` (${s.supporting_count})` : ""}
        </summary>
        <div className="mt-1.5 space-y-1 text-xs text-charcoal">
          {Object.entries(card.facts || {}).map(([k, v]) => (
            <p key={k}><span className="text-warmgrey">{k.replaceAll("_", " ")}:</span> {String(v)}</p>
          ))}
        </div>
        {(card.approved_guidance || []).map((g) => (
          <GovernedLink key={g.knowledge_id} target={g.target} testId="copilot-technical-record-link"
            className="mt-1 block text-[11px] text-terracotta hover:underline">
            Open record {g.knowledge_id} — {g.topic}
          </GovernedLink>
        ))}
        {!s && (card.unknowns || []).length > 0 && (
          <div className="mt-2 space-y-1 rounded-sm bg-amber-50 p-2" data-testid="copilot-technical-unknowns">
            {card.unknowns.map((u, i) => <p key={i} className="text-xs text-amber-800">{u}</p>)}
          </div>
        )}
        <p className="mt-2 text-[10px] text-warmgrey">Sources: {(card.sources || []).join(" · ")} · {card.scope}</p>
      </details>
      {(card.narrow_options || []).length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5" data-testid="copilot-technical-narrow">
          <span className="text-[10px] text-warmgrey self-center">Narrow to:</span>
          {card.narrow_options.map((o) => (
            <button key={o.sku} type="button" disabled={busy} onClick={() => onAsk?.(o.text)}
              className="rounded-full border border-border px-2 py-0.5 text-[10px] font-medium text-charcoal hover:border-terracotta hover:text-terracotta disabled:opacity-60"
              data-testid={`copilot-technical-narrow-${o.sku}`}>{o.sku} · {o.format_in}</button>
          ))}
        </div>
      )}
    </div>
  );
}

// RC48-TECHUX-004/005: supporting knowledge/evidence cards are progressive detail —
// one visible by default, the rest behind an explicit control (never a mobile flood).
function SupportingRecords({ cards, onAsk, busy }) {
  const [open, setOpen] = useState(false);
  const shown = open ? cards : cards.slice(0, 1);
  return (
    <div className="space-y-2" data-testid="copilot-supporting-records">
      {shown.map((c, i) => <RecordCard key={c.knowledge_id || c.evidence_id || i} card={c} onAsk={onAsk} busy={busy} />)}
      {cards.length > 1 && (
        <button type="button" onClick={() => setOpen(!open)} aria-expanded={open} className={`text-[11px] font-medium text-terracotta hover:underline ${FOCUS_RING}`}
          data-testid="copilot-supporting-toggle">
          {open ? "Hide supporting records" : `Show ${cards.length - 1} more supporting record${cards.length > 2 ? "s" : ""}`}
        </button>
      )}
    </div>
  );
}

const NBA_CLASS = {
  helpful_suggestion: { label: "Helpful suggestion", style: "bg-sand text-warmgrey border-border" },
  action_needed: { label: "Action needed", style: "bg-orange-50 text-orange-700 border-orange-200" },
  time_sensitive: { label: "Time sensitive", style: "bg-red-50 text-red-700 border-red-200" },
};

const NBA_SIGNAL_TEXT = {
  active_reservation: "active reservation on record",
  expires_within_24h: "authoritative expiration within 24 hours",
  conversion_blocked: "conversion currently blocked",
  repeat_hold_pending: "repeat hold awaiting approval",
  no_inventory_held: "no inventory held by the request",
  follow_up_date_today: "confirmed follow-up date is today",
  follow_up_date_overdue: "confirmed follow-up date is overdue",
  confirmed_project_fact: "confirmed Project record",
  confirmed_next_step_open: "confirmed next step outstanding",
  selected_variant_unavailable: "selected product unavailable for new transactions",
  technical_review_before_order: "technical/selection review required before ordering",
  availability_below_requirement: "current availability below project requirement",
  no_silent_substitution: "no automatic product/quantity substitution",
  project_requirement_resolved: "project requirement is resolved",
  availability_covers_requirement: "current supply covers the requirement",
  sample_shipped_not_delivered: "shipped — delivery not authoritatively confirmed",
  sample_partially_sent: "partially sent — units still open",
  sample_delivered: "authoritatively delivered",
  sample_needs_sourcing: "flagged for sourcing",
  sample_open: "open sample request",
  payment_pending: "payment pending — not paid",
  payment_failed: "last payment attempt failed",
  ach_processing: "ACH processing — not settled",
  dc_deposit_pending: "30% deposit outstanding",
  dc_balance_due: "70% balance due",
  deposit_paid_distinct: "deposit already paid — balance is a separate obligation",
};

const fmtDeadline = (s) => {
  if (!s || /^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const d = new Date(s.includes("T") ? s : s.replace(" ", "T") + "Z");
  return isNaN(d) ? s : `${d.toISOString().slice(0, 16).replace("T", " ")} UTC`;
};

// normalizes any embedded ISO/BSON datetimes inside free-text facts (e.g. reservation expiry)
const fmtFact = (s) => (!s ? s : s.replace(/(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2}):\d{2}(\.\d+)?/g, "$1 $2 UTC"));

function NbaCard({ card, onAct, onDismiss, onSnooze }) {
  return (
    <div className="rounded-md border border-border bg-white p-3" data-testid="copilot-nba-card">
      <p className="text-xs font-semibold uppercase tracking-wide text-terracotta">Suggested next steps (advisory)</p>
      {(card.items || []).length === 0 && (
        <p className="mt-1.5 text-xs text-warmgrey">{card.note || "Nothing needs attention right now."}</p>
      )}
      <div className="mt-1.5 space-y-2">
        {(card.items || []).map((item, i) => {
          const cls = NBA_CLASS[item.rec_class] || NBA_CLASS.helpful_suggestion;
          return (
            <div key={item.rec_key || i} className={`rounded-sm border p-2 ${cls.style}`} data-testid="copilot-nba-item">
              <div className="flex items-start justify-between gap-2">
                <span className="inline-block rounded-full border border-current px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide" data-testid="copilot-nba-class">
                  {cls.label}
                </span>
                <div className="flex shrink-0 gap-2">
                  <button type="button" onClick={() => onSnooze?.(item)}
                    className="text-[10px] text-warmgrey hover:text-charcoal" data-testid="copilot-nba-snooze">Snooze 24h</button>
                  <button type="button" onClick={() => onDismiss?.(item)}
                    className="text-[10px] text-warmgrey hover:text-charcoal" data-testid="copilot-nba-dismiss">Dismiss</button>
                </div>
              </div>
              <p className="mt-1 text-xs font-medium text-charcoal">{item.label}</p>
              <p className="text-xs" data-testid="copilot-nba-fact"><span className="font-medium">Fact:</span> {fmtFact(item.fact)}</p>
              {item.deadline_at && (
                <p className="text-[11px] font-medium" data-testid="copilot-nba-deadline">Deadline: {fmtDeadline(item.deadline_at)}</p>
              )}
              {item.blocker && (
                <p className="mt-0.5 text-[11px] font-medium text-red-700" data-testid="copilot-nba-blocker">{item.blocker}</p>
              )}
              <p className="mt-0.5 text-xs text-charcoal" data-testid="copilot-nba-action">
                <span className="font-medium">Suggested:</span> {item.suggested_action}
              </p>
              {(item.signals || []).length > 0 && (
                <details className="mt-0.5" data-testid="copilot-nba-why">
                  <summary className="cursor-pointer text-[11px] text-warmgrey hover:text-charcoal">Why this — approved signals</summary>
                  <ul className="ml-3 list-disc text-[11px] text-warmgrey">
                    {item.signals.map((s) => <li key={s}>{NBA_SIGNAL_TEXT[s] || s}</li>)}
                  </ul>
                </details>
              )}
              <div className="mt-1 flex items-center gap-3">
                {item.route && (
                  <GovernedLink target={item.route} testId="copilot-nba-route" className="inline-block text-[11px] font-medium text-terracotta hover:underline">
                    Open in {item.kind === "project" ? "Projects" : item.kind === "sample" ? "Samples" : item.kind === "order" ? "Orders" : "Reservations"}
                  </GovernedLink>
                )}
                {item.continuation?.tool && (
                  <button type="button" onClick={() => onAct?.(i)}
                    className="text-[11px] font-medium text-terracotta hover:underline" data-testid="copilot-nba-act">
                    Ask ZELI to prepare this
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {card.ranking_note && (
        <p className="mt-2 text-[10px] italic text-warmgrey" data-testid="copilot-nba-ranking-note">{card.ranking_note}</p>
      )}
      {card.incomplete && (
        <p className="mt-2 text-[10px] font-medium text-orange-700" data-testid="copilot-nba-incomplete">
          Prioritization incomplete — some signals were unavailable; nothing was fabricated to fill the gap.
        </p>
      )}
      {(card.items || []).length > 0 && (
        <>
          <p className="mt-2 text-[10px] text-warmgrey">{card.note}</p>
          <p className="mt-0.5 text-[10px] text-warmgrey">
            Advisory AI recommendations — not system notifications. Dismiss/snooze only hides a suggestion; mandatory reservation/payment alerts are unaffected.
          </p>
        </>
      )}
    </div>
  );
}

// COP-UX-158/159, COP-ARCH-211: contextual next actions rendered from the card's
// deterministic capability metadata — links open the canonical workflow; ask-actions
// re-enter the governed prepare→confirm conversation (no bypass, continuity kept).
// RC49-COP-001…003: labels come from the server (destination-aware); a link whose target
// is not an authorized in-app route shows a visible error instead of doing nothing.
function CardActions({ card, onAsk, busy }) {
  const [pending, setPending] = useState(-1);
  useEffect(() => { if (!busy) setPending(-1); }, [busy]);
  // V5-D NAV-007/008: only server-built governed destinations render; no legacy card.link, no "#"
  const actions = (card.actions || []).filter((a) => a && (a.kind !== "link" || isGovernedTarget(a.target)));
  if (!actions.length) return null;
  return (
    <div className="mt-2 flex flex-wrap gap-2" data-testid="copilot-card-actions">
      {actions.map((a, i) =>
        a.kind === "link" ? (
          <GovernedLink key={i} target={a.target}
            className={`rounded-full border border-terracotta/40 px-3 py-1 text-[11px] font-medium text-terracotta transition-colors hover:bg-terracotta hover:text-white ${FOCUS_RING}`}>
            {a.label}
          </GovernedLink>
        ) : (
          <button key={i} type="button" disabled={busy} aria-disabled={busy}
            onClick={() => { if (busy) return; setPending(i); onAsk?.(a.text); }}
            className={`inline-flex items-center gap-1 rounded-full border border-border bg-white px-3 py-1 text-[11px] font-medium text-charcoal transition-colors hover:border-terracotta hover:text-terracotta disabled:opacity-60 ${FOCUS_RING}`}
            data-testid="copilot-card-action">
            {pending === i && busy && <Loader2 className="h-3 w-3 animate-spin motion-reduce:animate-none" aria-hidden="true" />}
            {a.label}
          </button>
        )
      )}
    </div>
  );
}

// V5-D NAV-002/003: navigation, pricing and document-binding cards — facts stay on the card,
// the destination comes from the server contract (identifiers only, never values in the URL).
// V5-H4 ERR-003: a card advertising a destination without a governed target shows truthful text, never a dead link.
function hasGovernedLink(card) {
  return (card.actions || []).some((a) => a?.kind === "link" && isGovernedTarget(a.target));
}
function DestinationUnavailable({ testId }) {
  return <p className="mt-2 text-[11px] text-warmgrey" data-testid={testId}>Destination not available — this record isn't released or authorized for your role or location.</p>;
}
function NavigationCard({ card }) {
  return (
    <div className="rounded-md border border-border bg-white p-3" data-testid="copilot-navigation-card">
      <p className="break-words text-sm font-medium text-charcoal">{card.title}</p>
      {hasGovernedLink(card) ? <CardActions card={card} /> : <DestinationUnavailable testId="copilot-navigation-unavailable" />}
    </div>
  );
}
function PricingCard({ card, onAsk, busy }) {
  const sp = card.selling_price || {};
  return (
    <div className="rounded-md border border-border bg-white p-3" data-testid="copilot-pricing-card">
      <p className="text-sm font-medium text-charcoal">{card.product_title}</p>
      <p className="text-xs text-warmgrey"><span className="mr-1 font-mono font-semibold text-charcoal" data-testid="copilot-card-sku">{card.sku}</span>{card.box_coverage_sqft ? `${card.box_coverage_sqft} sq ft/box` : ""}</p>
      <p className="mt-1 text-xs text-charcoal" data-testid="copilot-pricing-values">
        {sp.unavailable ? "Selling price not set" : `Selling ${sp.per_sqft != null ? `$${Number(sp.per_sqft).toFixed(2)}/sq ft` : ""}${sp.per_box != null ? ` · $${Number(sp.per_box).toFixed(2)}/box` : ""}`}
      </p>
      <CardActions card={card} onAsk={onAsk} busy={busy} />
    </div>
  );
}
function BindingCard({ card }) {
  return (
    <div className="rounded-md border border-border bg-white p-3" data-testid="copilot-binding-card" data-state={card.state}>
      <p className="text-sm font-medium text-charcoal">{card.doc_number}{card.revision ? ` · Rev ${card.revision}` : ""}</p>
      <p className="mt-0.5 text-xs text-charcoal" data-testid="copilot-binding-message">{card.message}</p>
      <CardActions card={card} />
    </div>
  );
}

// V4.3 aggregate/summary card (COP-ARCH-196/197): explicit scope, period and metric
// labels travel with every aggregate; drill-down uses the canonical module route.
function SummaryCard({ card, onAsk, busy }) {
  return (
    <div className="rounded-md border border-border bg-white p-3 transition-colors hover:border-terracotta" data-testid="copilot-summary-card">
      <p className="text-xs font-semibold uppercase tracking-wide text-terracotta">{card.title}</p>
      <div className="mt-1.5 space-y-1">
        {(card.metrics || []).map((m) => (
          <p key={m.label} className="flex items-baseline justify-between gap-3 text-xs text-charcoal">
            <span className="text-warmgrey">{m.label}</span>
            <span className="num font-semibold" data-testid="copilot-summary-metric">{String(m.value)}</span>
          </p>
        ))}
      </div>
      {(card.attention || []).length > 0 && (
        <div className="mt-2 space-y-1 border-t border-border pt-2">
          {card.attention.map((a, i) => (
            <p key={i} className="text-xs text-charcoal"><span className="font-medium">{a.title}</span>{a.detail ? ` — ${a.detail}` : ""}</p>
          ))}
        </div>
      )}
      <p className="mt-2 text-[10px] text-warmgrey">{[card.scope, card.period].filter(Boolean).join(" · ")}</p>
      {card.definitions && <p className="mt-0.5 text-[10px] italic text-warmgrey">{card.definitions}</p>}
      <CardActions card={card} onAsk={onAsk} busy={busy} />
    </div>
  );
}

// V4.3 cross-module structured record cards (COP-UX-149, COP-ARCH-199): canonical
// identity stays inspectable and every card with an authorized target is clickable.
function RecordCard({ card, onAsk, busy }) {
  const eta = card.eta_warehouse || card.eta_carrier;
  const details = ({
    container: [card.status, eta ? `ETA ${String(eta).slice(0, 10)}` : (card.eta_message || "No ETA stored"),
                card.available_to_reserve_boxes != null && `${card.available_to_reserve_boxes} boxes open to reserve`,
                card.lines_count != null && `${card.lines_count} product lines`],
    quote: [card.status, card.project_name, card.total_usd != null && `$${Number(card.total_usd).toFixed(2)}`],
    custom_request: [card.status],
    sample: [card.status, card.lines_count != null && `${card.lines_count} line(s)`],
    reservation: [card.status, card.boxes != null && `${card.boxes} boxes`,
                  card.expires_at && `expires ${String(card.expires_at).slice(0, 16).replace("T", " ")} UTC`],
    order: [card.status, card.payment_state && `payment: ${card.payment_state}`,
            card.total_usd != null && `$${Number(card.total_usd).toFixed(2)}`],
    notification: [card.type, card.read ? "read" : "unread"],
    certificate: [card.status, card.detail],
    team: [card.role, card.location],
    media: [card.media_type, (card.applications || []).join(", ")],
    knowledge: [card.knowledge_kind, card.knowledge_id, card.verification_state],
    evidence: [card.current_documented_status,
               card.report_available ? "independent report registered" : "no independent report registered"],
  })[card.kind] || [];
  const knowledgeText = card.kind === "knowledge"
    ? (card.content?.value || card.content?.guidance || card.content?.position
       || card.content?.safe_external_position || card.content?.rule)
    : null;
  return (
    <div className="rounded-md border border-border bg-white p-3 transition-colors hover:border-terracotta" data-testid={`copilot-${card.kind}-card`}>
      <p className="break-words text-sm font-medium text-charcoal">{card.label || card.title}</p>
      <p className="mt-0.5 break-words text-xs text-warmgrey">{details.filter(Boolean).join(" · ")}</p>
      {knowledgeText && <p className="mt-1 break-words text-xs text-charcoal">{knowledgeText}</p>}
      {(card.actions || []).some((a) => a?.kind === "link") && !hasGovernedLink(card) && <DestinationUnavailable testId="copilot-record-unavailable" />}
      {card.kind === "knowledge" && card.content?.required_qualification && (
        <p className="mt-0.5 text-[11px] italic text-amber-800">{card.content.required_qualification}</p>
      )}
      {card.kind === "evidence" && card.what_may_be_communicated && (
        <p className="mt-1 text-xs text-charcoal">{card.what_may_be_communicated}</p>
      )}
      <CardActions card={card} onAsk={onAsk} busy={busy} />
    </div>
  );
}

export default function CopilotPanel({ variant = "workspace" }) {
  const { customerView, awaitModeSync } = useCustomerView();
  const { sessionId, setSessionId, panelContext } = useCopilot();
  const [session, setSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [listening, setListening] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  // V5-G2 (VCE-003/004/010): truthful voice lifecycle — idle | requesting | recording | transcribing | review | submitting | cancelled | error
  const [voiceState, setVoiceState] = useState("idle");
  const [voiceEnabled, setVoiceEnabled] = useState(true);
  const [voiceNote, setVoiceNote] = useState("");
  const [interim, setInterim] = useState("");
  const [elapsed, setElapsed] = useState(0);
  const cancelRef = useRef(false);
  const micBusyRef = useRef(false);
  const transcribeAbortRef = useRef(null);
  const recognitionRef = useRef(null);
  const preTranscriptRef = useRef(null);
  const elapsedTimerRef = useRef(null);
  const interimSupported = typeof window !== "undefined" && !!(window.SpeechRecognition || window.webkitSpeechRecognition);
  const [playingId, setPlayingId] = useState(null);
  const [copilotDown, setCopilotDown] = useState(false);
  const [preparingId, setPreparingId] = useState(null);
  const [reportOpen, setReportOpen] = useState(false);
  const [reportText, setReportText] = useState("");
  const [reportSending, setReportSending] = useState(false);
  const [showJump, setShowJump] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const audioRef = useRef(null);
  const listRef = useRef(null);
  const idemRef = useRef(0);
  const followingRef = useRef(true);
  const speakAbortRef = useRef(null);
  const inputRef = useRef(null);
  const sendIntentRef = useRef(false);
  const inputValueRef = useRef("");
  // V5-H4 ACC-005/006: one polite announcement (newest change only, deduped) + report-form focus restoration
  const [announcement, setAnnouncement] = useState("");
  const announcedRef = useRef(new Set());
  const reportButtonRef = useRef(null);
  const reportInputRef = useRef(null);
  const announce = useCallback((text) => {
    if (!text) return;
    setAnnouncement("");  // clear first so an identical consecutive message is still announced once
    setTimeout(() => setAnnouncement(text), 30);
  }, []);
  const focusComposer = useCallback(() => inputRef.current?.focus(), []);

  const context = session?.context || panelContext;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const h = await api.get("/copilot/health");
        if (!h.data?.enabled) setCopilotDown(true);
        if (h.data?.voice_enabled === false) setVoiceEnabled(false);
      } catch { setCopilotDown(true); }
      if (sessionId) {
        try {
          const r = await api.get(`/copilot/sessions/${sessionId}`);
          if (cancelled) return;
          setSession(r.data);
          setMessages(r.data.messages || []);
          (r.data.messages || []).forEach((m) => announcedRef.current.add(m.id));  // ACC-005: history is never re-announced
        } catch {
          if (!cancelled) { setSessionId(null); setSession(null); setMessages([]); }
        }
      } else {
        setSession(null);
        setMessages([]);
      }
    })();
    return () => { cancelled = true; };
    // NAV-012: a Customer Mode toggle re-reads the governed (mode-filtered) transcript — trade cards never linger
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, customerView]);

  useEffect(() => {
    (async () => {
      try {
        const params = context?.kind ? { kind: context.kind, id: context.id, label: context.label } : {};
        const r = await api.get("/copilot/suggested-prompts", { params });
        setSuggestions(r.data?.prompts || []);
      } catch { setSuggestions([]); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [context?.kind, context?.id, customerView]);

  // COP-UX-148: a newly rendered long answer opens at its BEGINNING; a user reading
  // earlier history is never yanked down — an unobtrusive jump-to-latest appears.
  const lastMsg = messages.length ? messages[messages.length - 1] : null;
  const lastMsgId = lastMsg?.id || null;
  useEffect(() => {
    const el = listRef.current;
    if (!el || !lastMsgId) return;
    if (followingRef.current) {
      const node = lastMsg?.role === "assistant" ? el.querySelector(`[data-msg-id="${lastMsgId}"]`) : null;
      if (node) node.scrollIntoView({ block: "start" });
      else el.scrollTo({ top: el.scrollHeight });
      setShowJump(false);
    } else {
      setShowJump(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastMsgId, sending]);

  // ACC-005: announce only a NEW completed assistant message (summary), never on restore/scroll/expand; focus is not moved.
  useEffect(() => {
    if (!lastMsg || lastMsg.role !== "assistant" || !lastMsgId || announcedRef.current.has(lastMsgId)) return;
    if (lastMsg.status !== "complete" && lastMsg.status !== "failed") return;
    announcedRef.current.add(lastMsgId);
    const text = (lastMsg.content || "").replace(/\s+/g, " ").trim();
    const summary = text.length > 180 ? `${text.slice(0, 180).trimEnd()}…` : text;
    const n = (lastMsg.cards || []).length;
    announce(`${lastMsg.status === "failed" ? "ZELI error: " : "ZELI: "}${summary}${n ? ` ${n} result card${n > 1 ? "s" : ""}.` : ""}`);
  }, [lastMsgId, lastMsg, announce]);

  // ACC-006: Report form — focus the input on open, restore to the Report control on close.
  useEffect(() => {
    if (reportOpen) reportInputRef.current?.focus();
    else if (reportButtonRef.current && document.activeElement && !document.body.contains(document.activeElement)) reportButtonRef.current.focus();
  }, [reportOpen]);
  const closeReport = useCallback(() => {
    setReportOpen(false);
    setTimeout(() => reportButtonRef.current?.focus(), 0);
  }, []);

  const onListScroll = useCallback(() => {
    const el = listRef.current;
    if (!el) return;
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    followingRef.current = nearBottom;
    if (nearBottom) setShowJump(false);
  }, []);

  // COP-UX-145 / COP-VTM-093: the input grows upward to a viewport-capped height,
  // then scrolls internally — long dictation stays fully visible and editable.
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = "auto";
    const cap = Math.min(220, Math.round(window.innerHeight * 0.35));
    el.style.height = `${Math.min(el.scrollHeight, cap)}px`;
    el.style.overflowY = el.scrollHeight > cap ? "auto" : "hidden";
  }, [input]);

  const patchActionCard = useCallback((actionId, newCard) => {
    setMessages((ms) => ms.map((m) => ({
      ...m,
      cards: (m.cards || []).map((c) => (c.kind === "action" && c.action_id === actionId ? newCard : c)),
    })));
  }, []);

  const appendResultMessage = useCallback((msg) => {
    if (!msg || !msg.id) return;
    setMessages((m) => (m.some((x) => x.id === msg.id) ? m : [...m, msg]));
  }, []);

  // BP4: dismiss/snooze writes AI-recommendation presentation state ONLY
  // (COP-NBA-073/075) — the card item is hidden locally; no business record changes.
  const nbaMutate = useCallback(async (item, mode) => {
    try {
      await api.post(`/copilot/recommendations/${mode}`, {
        rec_key: item.rec_key,
        fingerprint: item.fingerprint || "",
        urgency: item.urgency || "normal",
        ...(mode === "snooze" ? { hours: 24 } : {}),
      });
      setMessages((prev) => prev.map((m) => ({
        ...m,
        cards: (m.cards || []).map((c) => (c.kind === "nba"
          ? { ...c, items: (c.items || []).filter((i) => i.rec_key !== item.rec_key) }
          : c)),
      })));
      toast.success(mode === "snooze"
        ? "Suggestion snoozed for 24h — the underlying record is unchanged."
        : "Suggestion dismissed — the underlying record is unchanged.");
    } catch {
      toast.error("Could not update the suggestion — nothing was changed.");
    }
  }, []);

  const stopAudio = useCallback(() => {
    speakAbortRef.current?.abort();  // cancel in-flight speech preparation (COP-VTM-098)
    speakAbortRef.current = null;
    if (audioRef.current) {
      audioRef.current.onended = null;  // V5-G2 barge-in: a stopped clip never resumes or re-fires
      audioRef.current.pause();
      audioRef.current.src = "";
    }
    audioRef.current = null;
    setPlayingId(null);
    setPreparingId(null);
  }, []);

  // V5-G2: provisional interim text (display-only; never sent) where the platform supports it
  const startInterim = useCallback(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return;
    try {
      const rec = new SR();
      rec.interimResults = true;
      rec.continuous = true;
      rec.onresult = (e) => {
        let txt = "";
        for (let i = 0; i < e.results.length; i += 1) txt += e.results[i][0].transcript;
        setInterim(txt.trim());
      };
      rec.onerror = () => {};
      rec.start();
      recognitionRef.current = rec;
    } catch { recognitionRef.current = null; }
  }, []);

  const stopInterim = useCallback(() => {
    try { recognitionRef.current?.stop(); } catch { /* already stopped */ }
    recognitionRef.current = null;
    setInterim("");
  }, []);

  const clearElapsed = useCallback(() => {
    if (elapsedTimerRef.current) clearInterval(elapsedTimerRef.current);
    elapsedTimerRef.current = null;
    setElapsed(0);
  }, []);

  const settleVoice = useCallback((state, note = "") => {
    setVoiceState(state);
    setVoiceNote(note);
    if (state === "cancelled" || state === "error") {
      setTimeout(() => setVoiceState((s) => (s === state ? "idle" : s)), state === "cancelled" ? 2500 : 6000);
    }
  }, []);

  // V5.1 WP6 (RC51-VCE-005 / HRA51-003): fire-and-forget voice lifecycle + timing telemetry. Never blocks the UI,
  // never carries audio or transcript text — counts, reasons, container and millisecond marks only.
  const voiceMarksRef = useRef({});
  const captureIdRef = useRef(null);
  const retryRef = useRef(0);
  const interruptedRef = useRef(false);
  const telemetry = useCallback((kind, extra = {}) => {
    const body = { session_id: sessionId || null, kind, capture_id: captureIdRef.current, client_ts: new Date().toISOString(), ...extra };
    api.post("/copilot/voice/telemetry", body).catch(() => {});
  }, [sessionId]);
  const mark = (name) => { voiceMarksRef.current[name] = performance.now(); };
  const since = (name) => (voiceMarksRef.current[name] ? Math.round(performance.now() - voiceMarksRef.current[name]) : undefined);

  const cancelVoice = useCallback(() => {
    // VCE-004: cancel recording / abort transcription — nothing is submitted, mic released
    const rec = recorderRef.current;
    if (voiceState === "recording" && rec && rec.state === "recording") {
      cancelRef.current = true;
      sendIntentRef.current = false;
      rec.stop();
      return;
    }
    if (voiceState === "transcribing") {
      cancelRef.current = true;
      sendIntentRef.current = false;
      transcribeAbortRef.current?.abort();
    }
  }, [voiceState]);

  const discardTranscript = useCallback(() => {
    // VCE-004: drop an unsubmitted transcript, restoring what was typed before the recording
    const prior = preTranscriptRef.current ?? "";
    setInput(prior);
    inputValueRef.current = prior;
    preTranscriptRef.current = null;
    telemetry("voice_transcript_discarded");
    settleVoice("cancelled", "Transcript discarded — nothing was sent.");
    inputRef.current?.focus();
  }, [settleVoice, telemetry]);

  // WP6 V8 (VR-002 / HRA51-003): OS/browser interruption (call, app switch, tab hidden) while recording finalises the capture
  // as a reviewable transcript — never a hung "recording" state; playback is stopped and reported, the written answer stays.
  useEffect(() => {
    const onHidden = () => {
      if (document.visibilityState !== "hidden") return;
      const rec = recorderRef.current;
      if (rec && rec.state === "recording") {
        interruptedRef.current = true;
        telemetry("voice_capture_interrupted", { reason: "page_hidden", metrics: { utterance_ms: since("recording") } });
        rec.stop();
      }
      if (audioRef.current) {
        telemetry("voice_playback_interrupted", { reason: "page_hidden" });
        stopAudio();
      }
    };
    document.addEventListener("visibilitychange", onHidden);
    window.addEventListener("pagehide", onHidden);
    return () => { document.removeEventListener("visibilitychange", onHidden); window.removeEventListener("pagehide", onHidden); };
  }, [telemetry, stopAudio]);

  const ensureSession = useCallback(async () => {
    if (sessionId) return sessionId;
    const payload = context?.kind ? { context: { kind: context.kind, id: context.id, label: context.label } } : {};
    const r = await api.post("/copilot/sessions", payload);
    setSessionId(r.data.id);
    setSession(r.data);
    return r.data.id;
  }, [sessionId, context, setSessionId]);

  const send = useCallback(async (text, channel = "text") => {
    // UI-COP-011/012 + COP-ARCH-213: Send during active listening is the second voice
    // path — stop capture, finalize the transcript, submit exactly once. Repeated or
    // double taps collapse onto the single in-flight intent (never a duplicate).
    if (listening) {
      if (sendIntentRef.current || transcribing || sending) return;
      sendIntentRef.current = true;
      setFinalizing(true);
      stopInterim();
      const rec = recorderRef.current;
      if (rec && rec.state === "recording") {
        rec.stop();  // onstop finalizes the transcript and performs the one submission
      } else {
        sendIntentRef.current = false;  // recorder already ended — nothing to submit
        setFinalizing(false);
        setListening(false);
        settleVoice("idle");
      }
      return;
    }
    const content = (text ?? input).trim();
    if (!content || sending) return;
    setSending(true);
    if (channel === "voice") { setVoiceState("submitting"); telemetry("voice_submitted", { metrics: { transcript_chars: content.length } }); }
    preTranscriptRef.current = null;
    setInput("");
    inputValueRef.current = "";
    idemRef.current += 1;
    const idem = `fe-${Date.now()}-${idemRef.current}`;
    try {
      await awaitModeSync();
      const sid = await ensureSession();
      const r = await api.post(`/copilot/sessions/${sid}/messages`,
        { content, channel },
        { headers: { "Idempotency-Key": idem } });
      setMessages((m) => {
        const incoming = [r.data.user_message, r.data.assistant_message].filter(Boolean);
        const have = new Set(m.map((x) => x.id));
        // V5-E CNV-005/CFM-009: a card returned for an already-shown action supersedes the earlier render
        const latest = new Map(incoming.flatMap((x) => (x.cards || []).filter((c) => c.kind === "action" && c.action_id).map((c) => [c.action_id, c])));
        const prior = m.map((x) => ({ ...x, cards: (x.cards || []).map((c) => (c.kind === "action" && latest.has(c.action_id) ? latest.get(c.action_id) : c)) }));
        return [...prior, ...incoming.filter((x) => !have.has(x.id))];
      });
      if (r.data.assistant_message?.status === "failed") {
        toast.error("ZELI couldn't complete that — nothing was changed.");
      }
      if (channel === "voice") { mark("answer"); telemetry("voice_answer_received", { metrics: { stop_to_answer_ms: since("stop") } }); }
    } catch (e) {
      const code = e.response?.data?.detail?.code;
      if (code === "copilot_disabled") {
        setCopilotDown(true);
      } else if (e.response?.status === 429) {
        toast.error(e.response?.data?.detail?.message || "Too many requests — wait a moment.");
      } else {
        toast.error("ZELI is unreachable — your Connect workspace is unaffected.");
      }
      setInput(content); // never silently lose unsent text (COP-VTM-068)
    } finally {
      setSending(false);
      if (channel === "voice") setVoiceState("idle");
    }
  }, [input, sending, listening, transcribing, ensureSession, awaitModeSync, stopInterim, settleVoice, telemetry]);

  const AFFIRM_RE = /^\s*(yes|yeah|yep|ok|okay|confirm|go ahead|do it|proceed|approve|sure)\b/i;

  const toggleMic = useCallback(async () => {
    if (micBusyRef.current) return;  // VCE-005/007: rapid repeated taps collapse onto one transition
    if (listening) {
      // Stop-for-review (UI-COP-011): end capture and finalize an editable transcript
      // WITHOUT submission. Guarded so a second tap during teardown cannot double-fire.
      const rec = recorderRef.current;
      if (rec && rec.state === "recording") { micBusyRef.current = true; rec.stop(); }
      return;
    }
    if (voiceState === "transcribing" || voiceState === "requesting" || sending) return;
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
      telemetry("voice_unsupported", { reason: "no_mediarecorder" });
      settleVoice("error", "Voice capture isn't supported on this browser — typing works the same.");
      return;
    }
    micBusyRef.current = true;
    stopAudio();  // VCE-007 barge-in: playback stops before a new capture begins
    stopInterim();
    cancelRef.current = false;
    interruptedRef.current = false;
    captureIdRef.current = `cap-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    voiceMarksRef.current = {};
    mark("tap");
    if (voiceState === "error") retryRef.current += 1; else retryRef.current = 0;
    if (retryRef.current) telemetry("voice_retry", { reason: `retry_${retryRef.current}` });
    telemetry("voice_capture_requested");
    settleVoice("requesting", "");
    let stream;
    try {
      // COP-UX-147 / COP-VTM-095: getUserMedia runs ONLY on this intentional mic
      // activation; on a stable origin the browser reuses an already-granted
      // permission (no app-level permission ceremony; browser/OS indicators stay
      // authoritative). Tracks stop after each capture so the indicator is truthful.
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (e) {
      micBusyRef.current = false;
      const name = e?.name || "";
      telemetry(name === "NotAllowedError" || name === "SecurityError" ? "voice_mic_denied" : "voice_error", { reason: `getusermedia_${name || "unknown"}` });
      if (name === "NotAllowedError" || name === "SecurityError") settleVoice("error", "Microphone permission was denied — allow it in your browser, or keep typing.");
      else if (name === "NotFoundError" || name === "OverconstrainedError") settleVoice("error", "No microphone was found — you can keep typing; ZELI works the same.");
      else settleVoice("error", "Microphone unavailable — you can keep typing; ZELI works the same.");
      return;
    }
    try {
      const rec = new MediaRecorder(stream);
      chunksRef.current = [];
      if (preTranscriptRef.current === null || voiceState !== "review") preTranscriptRef.current = inputValueRef.current;  // discard restores pre-voice text across a review chain
      rec.ondataavailable = (e) => e.data.size && chunksRef.current.push(e.data);
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        clearElapsed();
        stopInterim();
        setListening(false);
        micBusyRef.current = false;
        mark("stop");
        const utteranceMs = since("recording");
        const mime = (rec.mimeType || "audio/webm").toLowerCase();
        if (cancelRef.current) {
          // VCE-004: cancelled — audio dropped, nothing transcribed, nothing sent
          cancelRef.current = false;
          chunksRef.current = [];
          sendIntentRef.current = false;
          setFinalizing(false);
          telemetry("voice_capture_cancelled", { metrics: { utterance_ms: utteranceMs }, container: mime });
          settleVoice("cancelled", "Recording cancelled — nothing was sent.");
          return;
        }
        telemetry("voice_capture_stopped", { reason: interruptedRef.current ? "interrupted" : (sendIntentRef.current ? "send_while_listening" : "stop_for_review"), metrics: { utterance_ms: utteranceMs }, container: mime });
        setTranscribing(true);
        setVoiceState("transcribing");
        const ctl = new AbortController();
        transcribeAbortRef.current = ctl;
        try {
          // VR-001: the container the browser actually produced (iOS Safari = audio/mp4) drives the file suffix —
          // a mislabelled container is rejected by the STT provider as "Invalid file format"
          const ext = mime.includes("mp4") || mime.includes("m4a") || mime.includes("aac") ? "m4a" : mime.includes("ogg") ? "ogg" : mime.includes("wav") ? "wav" : mime.includes("mpeg") || mime.includes("mp3") ? "mp3" : "webm";
          const blob = new Blob(chunksRef.current, { type: mime });
          if (blob.size === 0) throw Object.assign(new Error("empty_audio"), { emptyAudio: true });
          const fd = new FormData();
          fd.append("file", blob, `voice.${ext}`);
          const r = await api.post("/copilot/voice/transcribe", fd, { signal: ctl.signal });
          const text = (r.data?.transcript || "").trim();
          telemetry("voice_transcript_ready", { metrics: { stop_to_transcript_ms: since("stop"), transcript_chars: text.length, audio_bytes: blob.size, utterance_ms: utteranceMs }, container: mime });
          if (interruptedRef.current && text) {
            // V8: an OS/browser interruption never auto-submits — the transcript is kept for review
            interruptedRef.current = false;
            sendIntentRef.current = false;
            const merged = inputValueRef.current ? `${inputValueRef.current} ${text}` : text;
            setInput(merged);
            inputValueRef.current = merged;
            settleVoice("review", "Recording was interrupted — the transcript so far is ready to review. Nothing was sent.");
            inputRef.current?.focus();
          } else if (sendIntentRef.current) {
            sendIntentRef.current = false;
            const content = [inputValueRef.current.trim(), text].filter(Boolean).join(" ").trim();
            if (!content) {
              settleVoice("error", "Couldn't hear anything — nothing was sent. Try again or type instead.");
            } else if (AFFIRM_RE.test(text)) {
              // D-G1 / VCE-008: a spoken confirmation is never auto-submitted — review it first
              setInput(content);
              inputValueRef.current = content;
              settleVoice("review", "This sounds like a confirmation — review the transcript, then Send to confirm.");
              inputRef.current?.focus();
            } else {
              // Send-during-listening (UI-COP-012): the finalized transcript submits exactly once
              setInput("");
              inputValueRef.current = "";
              await send(content, "voice");
            }
          } else if (text) {
            // Stop-for-review: transcription is reviewed/editable in the composer before send (COP-VTM-016)
            const merged = inputValueRef.current ? `${inputValueRef.current} ${text}` : text;
            setInput(merged);
            inputValueRef.current = merged;
            settleVoice("review", "Transcript ready — review or edit, then Send.");
            inputRef.current?.focus();
          } else {
            settleVoice("error", "Couldn't hear anything — try again or type instead.");
          }
        } catch (e) {
          sendIntentRef.current = false;
          interruptedRef.current = false;
          if (ctl.signal.aborted) { telemetry("voice_capture_cancelled", { reason: "transcription_aborted" }); settleVoice("cancelled", "Transcription cancelled — nothing was sent."); }
          else if (e?.emptyAudio || e?.response?.data?.detail?.code === "empty_audio") { telemetry("voice_capture_empty", { metrics: { utterance_ms: utteranceMs }, container: mime }); settleVoice("error", "The microphone captured no audio — tap the mic and speak again, or type instead. Nothing was lost."); }
          else if (!e.response) { telemetry("voice_error", { reason: "transcription_network" }); settleVoice("error", "Network interrupted — the recording wasn't sent. Try again or type instead."); }
          else { telemetry("voice_error", { reason: `transcription_${e.response?.data?.detail?.code || e.response.status}` }); settleVoice("error", "Voice transcription unavailable — type instead. Nothing was lost."); }
        } finally {
          transcribeAbortRef.current = null;
          cancelRef.current = false;
          setTranscribing(false);
          setFinalizing(false);
        }
      };
      recorderRef.current = rec;
      // VR-002: browser/OS interruption (call, app switch, route change) ends the track — finalize instead of hanging in "recording"
      stream.getTracks().forEach((t) => { t.onended = () => { if (rec.state === "recording") rec.stop(); }; });
      rec.start();
      setListening(true);
      setVoiceState("recording");
      mark("recording");
      telemetry("voice_capture_started", { metrics: { mic_tap_to_recording_ms: since("tap") }, container: (rec.mimeType || "").toLowerCase() });
      setVoiceNote("");
      setElapsed(0);
      elapsedTimerRef.current = setInterval(() => setElapsed((s) => s + 1), 1000);
      startInterim();
      micBusyRef.current = false;
    } catch {
      stream.getTracks().forEach((t) => t.stop());
      micBusyRef.current = false;
      telemetry("voice_error", { reason: "mediarecorder_init" });
      settleVoice("error", "Microphone unavailable — you can keep typing; ZELI works the same.");
    }
  }, [listening, voiceState, sending, send, stopAudio, stopInterim, startInterim, clearElapsed, settleVoice, telemetry]);

  const speak = useCallback(async (messageId) => {
    if (playingId === messageId || preparingId === messageId) { stopAudio(); return; }
    stopAudio();
    mark("speak_tap");
    // COP-UX-154 / COP-VTM-098: synthesis can take perceptible time — an immediate
    // preparing state shows and a working cancel path exists throughout.
    setPreparingId(messageId);
    const ctl = new AbortController();
    speakAbortRef.current = ctl;
    try {
      const r = await api.get(`/copilot/messages/${messageId}/speak`, { responseType: "blob", signal: ctl.signal });
      const url = URL.createObjectURL(r.data);
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.onended = () => { setPlayingId(null); URL.revokeObjectURL(url); telemetry("voice_playback_stopped", { reason: "ended" }); };
      setPreparingId(null);
      setPlayingId(messageId);
      await audio.play();
      telemetry("voice_playback_started", { metrics: { answer_to_playback_ms: since("speak_tap") } });
    } catch (e) {
      setPreparingId(null);
      setPlayingId(null);
      if (ctl.signal.aborted || e?.name === "AbortError") { telemetry("voice_playback_interrupted", { reason: "user_stop" }); return; }  // deliberate cancel / barge-in — the written answer is untouched (COP-VTM-099)
      const code = e.response?.data?.detail?.code;
      telemetry("voice_error", { reason: `playback_${code || e.response?.status || "unavailable"}` });
      if (code === "mode_changed") toast.error("Switch back to Trade Mode to hear this response.");
      else toast.error("Audio unavailable right now — the written answer above is complete.");
    }
  }, [playingId, preparingId, stopAudio, telemetry]);

  // COP-UX-153 / COP-ARCH-200: application-owned accessible report form — the draft
  // survives failures, and success shows only after the server's authoritative
  // receipt with its traceable reference.
  const submitReport = async () => {
    if (!sessionId || reportSending || !reportText.trim()) return;
    setReportSending(true);
    try {
      // V5-H3 FBK-002: pass only correlation context we actually know — never invented values
      const lastUser = [...messages].reverse().find((m) => m.role === "user");
      const body = { note: reportText.trim() };
      if (lastUser?.channel === "voice" || lastUser?.channel === "text") body.channel = lastUser.channel;
      if (process.env.REACT_APP_CLIENT_BUILD) body.client_build = process.env.REACT_APP_CLIENT_BUILD;
      const r = await api.post(`/copilot/sessions/${sessionId}/report`, body);
      toast.success(`Report received by Connect — reference ${r.data?.reference || "logged"}.`);
      announce(`Report received by Connect — reference ${r.data?.reference || "logged"}.`);
      setReportText("");
      closeReport();
    } catch {
      toast.error("Couldn't send the report — your text is kept; please retry.");
      announce("Couldn't send the report — your text is kept; please retry.");
    } finally {
      setReportSending(false);
    }
  };

  if (copilotDown) {
    // COP-UX-152 / EVAL-172: compact truthful state, conversation preserved, real retry.
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 p-8 text-center" data-testid="copilot-unavailable">
        <p className="text-sm font-medium text-charcoal">ZELI is temporarily unavailable</p>
        <p className="text-xs text-warmgrey">Your conversation is preserved. Every Connect module keeps working — nothing here blocks your work.</p>
        <Button
          variant="outline"
          className="mt-2 border-terracotta/40 text-terracotta hover:bg-terracotta/10"
          data-testid="copilot-retry-button"
          onClick={async () => {
            try {
              const h = await api.get("/copilot/health");
              if (h.data?.enabled) setCopilotDown(false);
              else toast.error("Still unavailable — please try again shortly.");
            } catch {
              toast.error("Still unavailable — please try again shortly.");
            }
          }}
        >
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col" data-testid="copilot-panel">
      <LiveRegion message={announcement} />
      {context?.label && (
        <div className="border-b border-border bg-sand/60 px-4 py-2" data-testid="copilot-context-chip">
          <p className="text-xs text-warmgrey">
            Talking about <span className="font-medium text-charcoal">{context.label}</span>
            {context.kind === "project" ? " (active project)" : " (product)"}
          </p>
        </div>
      )}
      {customerView && (
        <div className="border-b border-border bg-limestone/40 px-4 py-1.5" data-testid="copilot-mode-chip">
          <p className="text-xs text-warmgrey">Customer Mode — answers stay customer-safe</p>
        </div>
      )}

      <div ref={listRef} onScroll={onListScroll} className="min-h-0 flex-1 space-y-4 overflow-y-auto px-4 py-4" data-testid="copilot-message-list">
        {messages.length === 0 && (
          <div className="space-y-3 pt-6">
            <p className="text-sm text-warmgrey">
              Ask about products, availability or your projects — type or tap the mic.
            </p>
            <div className="flex flex-wrap gap-2" data-testid="copilot-suggestions">
              {/* COP-UX-150: referential prompts need an existing result set — never shown in an empty thread */}
              {suggestions.filter((s) => !/^which of these/i.test(s)).map((s) => (
                <button
                  key={s}
                  onClick={() => send(s)}
                  disabled={sending}
                  aria-disabled={sending}
                  className={`min-h-[36px] rounded-full border border-border bg-white px-3 py-1.5 text-left text-xs text-charcoal transition-colors hover:border-terracotta hover:text-terracotta disabled:opacity-60 ${FOCUS_RING}`}
                  data-testid="copilot-suggested-prompt"
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m) => (
          <div key={m.id} data-msg-id={m.id} className={m.role === "user" ? "flex justify-end" : "flex justify-start"}>
            <div className={`max-w-[92%] space-y-2 md:max-w-[85%] ${m.role === "user" ? "text-right" : ""}`}>
              <div
                className={`inline-block rounded-lg px-3 py-2 text-left text-sm ${
                  m.role === "user"
                    ? "bg-terracotta text-white"
                    : m.status === "failed"
                      ? "border border-red-200 bg-red-50 text-charcoal"
                      : "border border-border bg-white text-charcoal"
                }`}
                data-testid={m.role === "user" ? "copilot-user-message" : m.status === "failed" ? "copilot-error-message" : "copilot-assistant-message"}
                role={m.role === "assistant" && m.status === "failed" ? "alert" : undefined}
              >
                {m.role === "assistant" && m.status === "failed" && (
                  <p className="mb-1 flex items-center gap-1 text-xs font-medium text-red-700"><AlertTriangle className="h-3 w-3" aria-hidden="true" /> Error — nothing was changed</p>
                )}
                {m.role === "assistant"
                  ? <LongText text={m.content} />
                  : <p className="whitespace-pre-wrap break-words">{renderText(m.content)}</p>}
                {m.channel === "voice" && m.role === "user" && (
                  <p className="mt-1 text-[10px] uppercase tracking-wide opacity-70">voice</p>
                )}
              </div>
              {m.role === "assistant" && m.status === "complete" && (
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => (playingId === m.id || preparingId === m.id ? stopAudio() : speak(m.id))}
                    className={`inline-flex min-h-[32px] items-center gap-1 text-xs text-warmgrey transition-colors hover:text-terracotta ${FOCUS_RING}`}
                    data-testid="copilot-listen-button"
                    aria-live="polite"
                  >
                    {preparingId === m.id
                      ? <Loader2 className="h-3 w-3 animate-spin motion-reduce:animate-none" aria-hidden="true" />
                      : playingId === m.id ? <Square className="h-3 w-3" /> : <Volume2 className="h-3 w-3" />}
                    {preparingId === m.id ? "Preparing audio… tap to cancel" : playingId === m.id ? "Stop" : "Listen"}
                  </button>
                </div>
              )}
              {(m.cards || []).length > 0 && (
                <div className="grid grid-cols-1 gap-2" data-testid="copilot-cards">
                  {(() => {
                    const supporting = m.cards.filter((c) => c.supporting && ["knowledge", "evidence"].includes(c.kind));
                    const rest = m.cards.filter((c) => !supporting.includes(c));
                    const critical = rest.filter((c) => NEVER_COLLAPSE.has(c.kind));
                    const listable = rest.filter((c) => !NEVER_COLLAPSE.has(c.kind));
                    const renderCard = (c, i) => {
                    const key = `${c.kind}-${c.variant_id || c.project_id || c.sku || i}-${i}`;
                    if (c.kind === "project") return <ProjectCard key={key} card={c} onAsk={send} busy={sending} />;
                    if (c.kind === "calculation") return <CalculationCard key={key} card={c} />;
                    if (c.kind === "estimate") return <EstimateCard key={key} card={c} />;
                    if (c.kind === "technical") return <TechnicalCard key={key} card={c} onAsk={send} busy={sending} />;
                    if (c.kind === "nba") return <NbaCard key={key} card={c} onAct={(idx) => send(`do recommendation ${idx + 1}`)} onDismiss={(item) => nbaMutate(item, "dismiss")} onSnooze={(item) => nbaMutate(item, "snooze")} />;
                    if (c.kind === "action") return <ActionCard key={c.action_id || key} card={c} onPatch={patchActionCard} onResult={appendResultMessage} onAnnounce={announce} onFocusComposer={focusComposer} />;
                    if (c.kind === "summary") return <SummaryCard key={key} card={c} onAsk={send} busy={sending} />;
                    if (c.kind === "navigation") return <NavigationCard key={key} card={c} />;
                    if (c.kind === "pricing") return <PricingCard key={key} card={c} onAsk={send} busy={sending} />;
                    if (c.kind === "document_binding") return <BindingCard key={key} card={c} />;
                    if (["container", "quote", "custom_request", "sample", "reservation", "order", "notification", "certificate", "team", "media", "knowledge", "evidence"].includes(c.kind)) return <RecordCard key={key} card={c} onAsk={send} busy={sending} />;
                    return <ProductCard key={key} card={c} onAsk={send} busy={sending} />;
                    };
                    return (<>
                  {critical.map(renderCard)}
                  <CardList items={listable} initial={3} noun={listable[0]?.kind === "project" ? "project" : listable.every((c) => !c.kind || c.kind === "product" || c.sku) ? "product" : "record"} render={renderCard} />
                  {supporting.length > 0 && <SupportingRecords cards={supporting} onAsk={send} busy={sending} />}
                    </>);
                  })()}
                </div>
              )}
            </div>
          </div>
        ))}
        {sending && (
          <div className="flex items-center gap-2 text-xs text-warmgrey" role="status" aria-live="polite" data-testid="copilot-processing-indicator">
            <Loader2 className="h-3.5 w-3.5 animate-spin text-terracotta motion-reduce:animate-none" aria-hidden="true" /> Checking authoritative Connect data…
          </div>
        )}
      </div>

      {showJump && (
        <div className="flex justify-center border-t border-border bg-sand/40 py-1.5">
          <button
            onClick={() => { followingRef.current = true; listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: "smooth" }); setShowJump(false); }}
            className={`inline-flex min-h-[32px] items-center gap-1 rounded-full border border-border bg-white px-3 py-1 text-xs text-charcoal shadow-sm transition-colors hover:border-terracotta hover:text-terracotta ${FOCUS_RING}`}
            data-testid="copilot-jump-to-latest"
          >
            <ArrowDown className="h-3 w-3" /> Jump to latest
          </button>
        </div>
      )}

      {/* COP-UX-144: visually distinct composer surface in the alabaster/charcoal/terracotta system */}
      <div className="border-t-2 border-terracotta/25 bg-sand/40 p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]" data-testid="copilot-composer" data-voice-state={voiceState}>
        {voiceState === "requesting" && (
          <p className="mb-1.5 flex items-center gap-1.5 text-xs text-warmgrey" role="status" aria-live="polite" data-testid="copilot-requesting-indicator">
            <Loader2 className="h-3 w-3 animate-spin motion-reduce:animate-none" /> Requesting microphone permission…
          </p>
        )}
        {listening && (
          <div className="mb-1.5" data-testid="copilot-listening-indicator">
            <p className="flex items-center gap-1.5 text-xs font-medium text-terracotta" role="status" aria-live="polite">
              <Mic className="h-3 w-3 animate-pulse motion-reduce:animate-none" /> Recording {Math.floor(elapsed / 60)}:{String(elapsed % 60).padStart(2, "0")} — tap the microphone to stop and review, Send to send, or Cancel to discard
            </p>
            {interimSupported ? (
              <p className="mt-1 rounded-sm border border-dashed border-terracotta/30 bg-white/70 px-2 py-1 text-xs italic text-charcoal" aria-live="off" data-testid="copilot-interim-transcript">
                <span className="not-italic text-[10px] font-medium uppercase tracking-wide text-warmgrey">Provisional — final transcript follows </span>
                {interim || "…"}
              </p>
            ) : (
              <p className="mt-1 text-[10px] text-warmgrey" data-testid="copilot-interim-unavailable">Live preview isn't available on this browser — the final transcript appears after you stop.</p>
            )}
          </div>
        )}
        {transcribing && (
          <p className="mb-1.5 flex items-center gap-1.5 text-xs text-warmgrey" role="status" aria-live="polite" data-testid="copilot-transcribing-indicator">
            <Loader2 className="h-3 w-3 animate-spin motion-reduce:animate-none" /> {finalizing ? "Finalizing and sending…" : "Transcribing…"}
            <button type="button" onClick={cancelVoice} className="ml-1 underline hover:text-charcoal" data-testid="copilot-voice-cancel-transcribing" aria-label="Cancel transcription">Cancel</button>
          </p>
        )}
        {voiceState === "review" && (
          <p className="mb-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-charcoal" role="status" aria-live="polite" data-testid="copilot-transcript-review">
            <span>{voiceNote}</span>
            <button type="button" onClick={discardTranscript} className="font-medium text-terracotta underline hover:text-terracotta-hover" data-testid="copilot-discard-transcript" aria-label="Discard transcript">Discard transcript</button>
          </p>
        )}
        {voiceState === "submitting" && (
          <p className="mb-1.5 flex items-center gap-1.5 text-xs text-warmgrey" role="status" aria-live="polite" data-testid="copilot-submitting-indicator">
            <Loader2 className="h-3 w-3 animate-spin motion-reduce:animate-none" /> Sending your spoken request…
          </p>
        )}
        {voiceState === "cancelled" && (
          <p className="mb-1.5 text-xs text-warmgrey" role="status" aria-live="assertive" data-testid="copilot-voice-cancelled">{voiceNote}</p>
        )}
        {voiceState === "error" && (
          <p className="mb-1.5 text-xs text-red-700" role="alert" data-testid="copilot-voice-error">{voiceNote}</p>
        )}
        {(playingId || preparingId) && (
          <p className="mb-1.5 flex items-center gap-1.5 text-xs text-warmgrey" role="status" aria-live="polite" data-testid="copilot-speaking-indicator">
            <Volume2 className="h-3 w-3" /> {preparingId ? "Preparing audio…" : "Speaking…"}
            <button type="button" onClick={stopAudio} className="ml-1 underline hover:text-charcoal" data-testid="copilot-stop-playback" aria-label="Stop playback">Stop</button>
          </p>
        )}
        {reportOpen && (
          <div className="mb-2 rounded-md border border-border bg-white p-2.5" data-testid="copilot-report-form" role="group" aria-labelledby="copilot-report-label"
            onKeyDown={(e) => { if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); closeReport(); } }}>
            <label id="copilot-report-label" htmlFor="copilot-report-text" className="text-xs font-medium text-charcoal">
              Report a problem — sent to Zelligio with this conversation's reference
            </label>
            <textarea
              id="copilot-report-text"
              ref={reportInputRef}
              value={reportText}
              onChange={(e) => setReportText(e.target.value)}
              rows={3}
              aria-describedby="copilot-report-hint"
              className="mt-1 w-full resize-none rounded-sm border border-border bg-background px-2 py-1.5 text-xs text-charcoal focus:border-terracotta focus:outline-none focus-visible:ring-2 focus-visible:ring-terracotta"
              data-testid="copilot-report-input"
            />
            <p id="copilot-report-hint" className="mt-0.5 text-[10px] text-warmgrey">Escape closes this form; your text is kept until sent. Credentials and card numbers are redacted automatically.</p>
            <div className="mt-1.5 flex justify-end gap-2">
              <button type="button" onClick={closeReport} className={`min-h-[32px] rounded-sm px-2 text-xs text-warmgrey hover:text-charcoal ${FOCUS_RING}`} data-testid="copilot-report-cancel">
                Cancel
              </button>
              <button
                type="button"
                onClick={submitReport}
                disabled={reportSending || !reportText.trim()}
                aria-disabled={reportSending || !reportText.trim()}
                className={`min-h-[32px] rounded-sm bg-terracotta px-3 py-1 text-xs font-medium text-white transition-colors hover:bg-terracotta-hover disabled:opacity-50 ${FOCUS_RING}`}
                data-testid="copilot-report-submit"
              >
                {reportSending ? "Sending…" : "Send report"}
              </button>
            </div>
          </div>
        )}
        <div className="flex items-end gap-2">
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => { inputValueRef.current = e.target.value; setInput(e.target.value); }}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
              if (e.key === "Escape" && (voiceState === "recording" || voiceState === "transcribing")) { e.preventDefault(); cancelVoice(); }
            }}
            aria-label={listening ? "Recording — tap the microphone to stop and review" : "Ask ZELI"}
            placeholder={listening ? "Recording… tap the microphone to stop and review" : "Ask ZELI…"}
            rows={1}
            disabled={sending}
            className="min-h-[40px] min-w-0 flex-1 resize-none rounded-md border border-border bg-white px-3 py-2.5 text-sm text-charcoal placeholder:text-warmgrey focus:border-terracotta focus:outline-none focus-visible:ring-2 focus-visible:ring-terracotta"
            data-testid="copilot-composer-input"
          />
          {(listening || voiceState === "requesting") && (
            <Button
              type="button"
              variant="outline"
              onClick={cancelVoice}
              onKeyDown={(e) => { if (e.key === "Escape") cancelVoice(); }}
              className={`h-10 w-10 shrink-0 border-border bg-white p-0 text-warmgrey hover:bg-sand ${FOCUS_RING}`}
              data-testid="copilot-voice-cancel-button"
              aria-label="Cancel recording — nothing will be sent"
            >
              <X className="h-4 w-4" />
            </Button>
          )}
          {voiceEnabled && (
          <Button
            type="button"
            variant="outline"
            onClick={toggleMic}
            disabled={sending || transcribing || voiceState === "requesting"}
            aria-busy={voiceState === "requesting" || transcribing}
            aria-pressed={listening}
            className={`h-10 w-10 shrink-0 p-0 ${FOCUS_RING} ${listening ? "border-red-400 bg-red-50 text-red-500" : "border-terracotta/40 bg-white text-terracotta hover:bg-terracotta/10"}`}
            data-testid="copilot-mic-button"
            aria-label={listening ? "Stop recording and review the transcript" : (playingId ? "Interrupt playback and speak to ZELI" : "Speak to ZELI")}
          >
            {listening ? <Square className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
          </Button>
          )}
          <Button
            type="button"
            onClick={() => send()}
            disabled={sending || transcribing || voiceState === "requesting" || (!listening && !input.trim())}
            aria-busy={sending}
            className={`h-10 w-10 shrink-0 bg-terracotta p-0 text-white hover:bg-terracotta-hover ${FOCUS_RING}`}
            data-testid="copilot-send-button"
            aria-label={listening ? "Stop recording and send" : "Send"}
          >
            {sending ? <Loader2 className="h-4 w-4 animate-spin motion-reduce:animate-none" aria-hidden="true" /> : <Send className="h-4 w-4" aria-hidden="true" />}
          </Button>
        </div>
        <div className="mt-1.5 flex items-center justify-between">
          <p className="text-[10px] text-warmgrey">Answers come from live Connect data. Verify critical numbers before committing.</p>
          <div className="flex items-center gap-3">
            {variant === "panel" && (
              <Link to="/dealer/copilot" className="text-xs font-medium text-terracotta hover:underline" data-testid="copilot-open-workspace">
                Open full workspace
              </Link>
            )}
            {sessionId && (
              <button type="button" ref={reportButtonRef} onClick={() => (reportOpen ? closeReport() : setReportOpen(true))} className={`inline-flex min-h-[32px] items-center gap-1 rounded-sm px-1 text-[10px] text-warmgrey hover:text-terracotta ${FOCUS_RING}`} data-testid="copilot-report-button" aria-expanded={reportOpen} aria-controls="copilot-report-text">
                <Flag className="h-3 w-3" aria-hidden="true" /> Report
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
